import { useState } from "react";
import { useNavigate, useParams, useRouter } from "@tanstack/react-router";
import { ChevronDown } from "lucide-react";
import { DE, ES, FR, IT, NL, US } from "country-flag-icons/react/3x2";
import { Button } from "./ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import type { SupportedLanguage } from "@/lib/i18n/init";
import { supportedLanguages } from "@/lib/i18n/init";
import { changeLanguage } from "@/lib/i18n/actions";

// Language configuration with flags and names
const languageConfig = {
  en: {
    Flag: US,
    name: "English",
    nativeName: "English",
  },
  nl: {
    Flag: NL,
    name: "Dutch",
    nativeName: "Nederlands",
  },
  fr: {
    Flag: FR,
    name: "French",
    nativeName: "Français",
  },
  de: {
    Flag: DE,
    name: "German",
    nativeName: "Deutsch",
  },
  it: {
    Flag: IT,
    name: "Italian",
    nativeName: "Italiano",
  },
  es: {
    Flag: ES,
    name: "Spanish",
    nativeName: "Español",
  },
} as const;

export function LanguageSwitcher() {
  const params = useParams({ strict: false });
  const navigate = useNavigate();
  const router = useRouter();
  const [isOpen, setIsOpen] = useState(false);

  const handleLanguageChange = (lang: string) => {
    // Get current path without locale prefix
    const currentPath = router.state.location.pathname;
    const pathWithoutLocale = currentPath.replace(/^\/[a-z]{2}/, '') || '/';
    
    // Navigate to the same page with new locale
    navigate({
      to: `/$locale${pathWithoutLocale}`,
      params: { locale: lang },
      search: router.state.location.search,
    });
    changeLanguage({ data: lang });
    setIsOpen(false);
  };

  // Use URL locale instead of i18n.language to ensure consistency
  const currentLanguage = params.locale as SupportedLanguage;
  // The `$locale` route redirects unsupported locales, but the switcher can
  // render mid-transition with a locale that has no config. Fall back to English
  // rather than crashing on `currentConfig.Flag`.
  const currentConfig =
    languageConfig[currentLanguage as keyof typeof languageConfig] ??
    languageConfig.en;

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="flex items-center gap-2 min-w-[120px] justify-between"
        >
          <div className="flex items-center gap-2">
            <currentConfig.Flag className="w-5 h-4 rounded-sm" />
            <span className="text-sm text-black">{currentConfig.nativeName}</span>
          </div>
          <ChevronDown className="h-4 w-4 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-48 max-h-[60vh] overflow-y-auto p-2 z-[99999]"
        align="end"
      >
        <div className="space-y-1">
          {supportedLanguages.map((lang) => {
            const config = languageConfig[lang as keyof typeof languageConfig];
            const isSelected = currentLanguage === lang;

            return (
              <Button
                key={lang}
                variant={isSelected ? "secondary" : "ghost"}
                size="sm"
                className="w-full justify-start gap-3 h-auto py-2"
                onClick={() => handleLanguageChange(lang)}
              >
                <config.Flag className="w-5 h-4 rounded-sm" />
                <div className="flex flex-col items-start">
                  <span className="text-sm font-medium">
                    {config.nativeName}
                  </span>
                  <span className="text-xs text-muted-foreground">
                    {config.name}
                  </span>
                </div>
                {isSelected && (
                  <div className="ml-auto w-2 h-2 bg-primary rounded-full" />
                )}
              </Button>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}
